import { useEffect, useRef } from "react";

import MailOutlinedIcon from "@mui/icons-material/MailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { Grid, InputAdornment } from "@mui/material";
import { FormikTextField, useResizableFieldMaxHeight } from "@shared";
import { FormikProps } from "formik";

import { AmbulanzType } from "@/api";
import { ClientSelect } from "@/common/components/client-select/ClientSelect";
import { formatClientOptionLabel } from "@/common/components/client-select/ClientSelect.utils";
import { FormikAutocompleteSelect } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocompleteSelect";
import { ReadOnlyClientField } from "@/common/components/read-only-client-field/ReadOnlyClientField";
import { nameof } from "@/common/helper/commonHelper";
import { useTranslation } from "@/translations";

import { AmbulanzFormValues, AmbulanzMaxLength, AmbulanzTypeOption } from "./AmbulanzForm.types";

interface Props {
  formik: FormikProps<AmbulanzFormValues>;
  typeOptions: AmbulanzTypeOption[];
  clientReadOnly?: boolean;
  clientId?: string | null;
  clientName?: string | null;
  detailsReadOnly?: boolean;
  noteReadOnly?: boolean;
  focusAmbulanzNumber?: boolean;
}

export function AmbulanzFormFields({
  formik,
  typeOptions,
  clientReadOnly = false,
  clientId,
  clientName,
  detailsReadOnly = false,
  noteReadOnly = false,
  focusAmbulanzNumber = false,
}: Props) {
  const { t } = useTranslation();
  const { setFieldRef, maxHeight } = useResizableFieldMaxHeight();
  const ambulanzNumberRef = useRef<HTMLInputElement>(null);

  const isAmbulanz = formik.values.type === AmbulanzType.Ambulanz;

  useEffect(() => {
    if (focusAmbulanzNumber) {
      ambulanzNumberRef.current?.focus();
    }
  }, [focusAmbulanzNumber]);

  return (
    <Grid container rowSpacing={2} columnSpacing={2} sx={{ pt: 2 }}>
      <Grid size={6}>
        <FormikAutocompleteSelect
          formik={formik}
          fieldName="type"
          label={t("ambulanzes.creationDialog.art")}
          options={typeOptions}
          required
          disableClearable
          disabled={detailsReadOnly}
        />
      </Grid>
      {isAmbulanz && (
        <Grid size={6}>
          <FormikTextField
            formik={formik}
            fieldName="ambulanzNumber"
            label={t("ambulanzes.creationDialog.ambulanzNumber")}
            required
            disabled={detailsReadOnly}
            inputRef={ambulanzNumberRef}
            slotProps={{ input: { inputProps: { maxLength: AmbulanzMaxLength.ambulanzNumber } } }}
          />
        </Grid>
      )}
      <Grid size={12}>
        {clientReadOnly ? (
          <ReadOnlyClientField clientId={clientId} clientName={clientName} />
        ) : (
          <ClientSelect
            label={t("common.client")}
            freeSolo
            onChange={(selection) => {
              if (selection && typeof selection !== "string") {
                void formik.setFieldValue(nameof<AmbulanzFormValues>("clientId"), selection.clientId);
                void formik.setFieldValue(nameof<AmbulanzFormValues>("clientName"), formatClientOptionLabel(selection));
                void formik.setFieldValue(nameof<AmbulanzFormValues>("phone"), selection.phone ?? "");
                void formik.setFieldValue(nameof<AmbulanzFormValues>("email"), selection.email ?? "");
              } else {
                void formik.setFieldValue(nameof<AmbulanzFormValues>("clientId"), null);
                void formik.setFieldValue(
                  nameof<AmbulanzFormValues>("clientName"),
                  typeof selection === "string" ? selection : null
                );
              }
            }}
          />
        )}
      </Grid>
      <Grid size={6}>
        <FormikTextField
          formik={formik}
          fieldName="phone"
          label={t("ambulanzes.creationDialog.phone")}
          disabled={detailsReadOnly}
          slotProps={{
            input: {
              inputProps: { maxLength: AmbulanzMaxLength.phone },
              endAdornment: (
                <InputAdornment position="end">
                  <PhoneOutlinedIcon fontSize="small" color="primary" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Grid>
      <Grid size={6}>
        <FormikTextField
          formik={formik}
          fieldName="email"
          label={t("ambulanzes.creationDialog.email")}
          disabled={detailsReadOnly}
          slotProps={{
            input: {
              inputProps: { maxLength: AmbulanzMaxLength.email },
              endAdornment: (
                <InputAdornment position="end">
                  <MailOutlinedIcon fontSize="small" color="primary" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Grid>
      <Grid size={12}>
        <FormikTextField
          formik={formik}
          fieldName="note"
          label={t("ambulanzes.creationDialog.note")}
          multiline
          rows={4}
          disabled={noteReadOnly}
          inputRef={setFieldRef}
          sx={{ "& .MuiInputBase-inputMultiline": { resize: "vertical", maxHeight } }}
          slotProps={{ input: { inputProps: { maxLength: AmbulanzMaxLength.note } } }}
        />
      </Grid>
    </Grid>
  );
}
